import React, { useState, useCallback } from 'react';
import { X } from 'lucide-react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  type EdgeProps,
  useReactFlow,
} from '@xyflow/react';

export function DeletableEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style,
  markerEnd,
  selected,
  data,
}: EdgeProps) {
  const { setEdges } = useReactFlow();
  const [clickPos, setClickPos] = useState<{ x: number; y: number } | null>(null);

  // Check if this edge is connected to a selected node
  const isHighlighted = data?.highlighted === true;

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Call the onDelete callback passed via edge data
    if (data?.onDelete) {
      (data.onDelete as (id: string) => void)(id);
    }
    setEdges((eds) => eds.filter((edge) => edge.id !== id));
  };

  // Track where user clicks on the edge path
  const handleEdgeClick = useCallback((e: React.MouseEvent<SVGPathElement>) => {
    // Get click position relative to SVG coordinate system
    const svg = e.currentTarget.closest('svg');
    if (svg) {
      const point = svg.createSVGPoint();
      point.x = e.clientX;
      point.y = e.clientY;
      const ctm = svg.getScreenCTM();
      if (ctm) {
        const svgPoint = point.matrixTransform(ctm.inverse());
        setClickPos({ x: svgPoint.x, y: svgPoint.y });
      }
    }
  }, []);

  // Use click position if available, otherwise fall back to center
  const buttonX = clickPos?.x ?? labelX;
  const buttonY = clickPos?.y ?? labelY;

  // Reset click position when deselected
  if (!selected && clickPos) {
    setClickPos(null);
  }

  // Compute edge style based on selected/highlighted state
  const edgeStyle = {
    ...style,
    ...(selected
      ? { stroke: '#10b981', strokeWidth: 3 }
      : isHighlighted
        ? { stroke: '#3b82f6', strokeWidth: 3 } // Blue highlight for connected edges
        : {}),
  };

  return (
    // Wrap in a group with higher zIndex when selected or highlighted
    <g style={{ zIndex: selected || isHighlighted ? 1000 : 0 }}>
      {/* Invisible wider path for easier clicking */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        onClick={handleEdgeClick}
        style={{ cursor: 'pointer' }}
      />
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={edgeStyle}
      />
      {selected && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${buttonX}px,${buttonY}px)`,
              pointerEvents: 'all',
              zIndex: 1000,
            }}
            className="nodrag nopan"
          >
            <button
              onClick={handleDelete}
              className="w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md border border-red-600"
              aria-label="Delete connection"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </EdgeLabelRenderer>
      )}
    </g>
  );
}
